import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import notifications from 'notifications';
import {
  ExecutableProcessTask,
  executeProcessTasksFromSearch,
  openProcessTaskWithWorksheet,
  getWorksheetUrlFromProcessTask,
} from 'processes/util/ProcessTaskExecutionUtil';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {withLoadingScreen} from 'util/Promises';

import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';

interface ExecuteButtonProps {
  // getTasksToExecute is responsible for returning tasks that can be executed
  getTasksToExecute: () => Promise<Array<ExecutableProcessTask>>;
  disabledMessage: string | null;
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void;
  actionOrigin?: ActionOrigin;
}

/**
 * Comparison function used to sort tasks within their respective task groups.
 * If comparing tasks from different task groups, order the task groups arbitrarily by task group ID.
 *
 * @param task1
 * @param task2
 * @returns Negative if task1 comes before task2, positive if task1 comes after task2, zero if equal sort order
 */
export const compareTaskGroupOrder = (task1: ExecutableProcessTask, task2: ExecutableProcessTask): number => {
  // Sort tasks in different task groups by task group ID
  const taskGroupComparisonValue = task1.process.id.localeCompare(task2.process.id);
  if (taskGroupComparisonValue !== 0) {
    return taskGroupComparisonValue;
  }

  return task1.sortValue.localeCompare(task2.sortValue);
};

export default function ExecuteButton({
  disabledMessage,
  getTasksToExecute,
  onTasksOrOutputsChange,
  actionOrigin,
}: ExecuteButtonProps): JSX.Element {
  return (
    <TooltipWrapper tooltipTitle={disabledMessage}>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        className={css(styles.button)}
        disabled={disabledMessage != null}
        size={ButtonSize.SMALL}
        text="Execute"
        onClick={async () => {
          const tasks = await withLoadingScreen(getTasksToExecute(), 'Executing tasks');
          const executableTasks = tasks.filter((task) => !isArchived(task));

          if (executableTasks.length === 0) {
            notifications.error('There are no tasks available to execute.');
            return;
          }
          if (executableTasks.length === 1 && getWorksheetUrlFromProcessTask(executableTasks[0])) {
            openProcessTaskWithWorksheet(executableTasks[0], actionOrigin);
          } else {
            executeProcessTasksFromSearch(executableTasks, onTasksOrOutputsChange, actionOrigin);
          }
        }}
        dataTestAttrs={{component: 'ProcessManagementToolbar', element: 'ExecuteTaskButton'}}
      />
    </TooltipWrapper>
  );
}

const styles = StyleSheet.create({
  button: {
    borderWidth: '0', // Override `.buttonGroup > .button { border-width: 1px; }`
  },
});
